import {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { Box } from "@mui/material";

interface CloudflareTurnstileProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  theme?: "light" | "dark";
  size?: "normal" | "compact" | "invisible";
  language?: string;
}

interface CloudflareTurnstileRef {
  reset: () => void;
}

declare global {
  interface Window {
    turnstile: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          theme?: "light" | "dark";
          size?: "normal" | "compact" | "invisible";
          language?: string;
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
        }
      ) => string;
      reset: (widgetId: string) => void;
    };
  }
}

const CloudflareTurnstile = forwardRef<
  CloudflareTurnstileRef,
  CloudflareTurnstileProps
>(
  (
    {
      siteKey,
      onVerify,
      onError,
      onExpire,
      theme = "light",
      size = "normal",
      language = "en",
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);

    // Use refs to store callbacks to prevent re-rendering
    const onVerifyRef = useRef(onVerify);
    const onErrorRef = useRef(onError);
    const onExpireRef = useRef(onExpire);

    // Update refs when callbacks change
    useEffect(() => {
      onVerifyRef.current = onVerify;
      onErrorRef.current = onError;
      onExpireRef.current = onExpire;
    }, [onVerify, onError, onExpire]);

    const renderWidget = useCallback(() => {
      console.log("renderWidget called", {
        turnstile: !!window.turnstile,
        container: !!containerRef.current,
        widgetId: widgetIdRef.current,
        siteKey,
      });

      if (window.turnstile && containerRef.current && !widgetIdRef.current) {
        try {
          // Clear any existing content
          if (containerRef.current) {
            containerRef.current.innerHTML = "";
          }

          console.log("Rendering Turnstile widget...");
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            theme,
            size,
            language,
            callback: (token: string) => {
              console.log("Turnstile callback triggered with token");
              onVerifyRef.current(token);
            },
            "expired-callback": () => {
              console.log("Turnstile expired");
              onExpireRef.current?.();
            },
            "error-callback": () => {
              console.log("Turnstile error");
              onErrorRef.current?.();
            },
          });

          console.log(
            "Turnstile widget rendered successfully",
            widgetIdRef.current
          );
        } catch (error) {
          console.error("Failed to render Turnstile:", error);
          onErrorRef.current?.();
        }
      } else {
        console.log("Cannot render widget:", {
          turnstile: !!window.turnstile,
          container: !!containerRef.current,
          widgetId: widgetIdRef.current,
        });
      }
    }, [siteKey, theme, size, language]); // Remove callback dependencies

    useEffect(() => {
      console.log("CloudflareTurnstile mounted");

      // Check if Turnstile is already loaded
      if (window.turnstile) {
        renderWidget();
      } else {
        // Wait for Turnstile to load
        const checkTurnstile = () => {
          if (window.turnstile && !widgetIdRef.current) {
            renderWidget();
          } else if (!widgetIdRef.current) {
            setTimeout(checkTurnstile, 100);
          }
        };
        checkTurnstile();
      }

      return () => {
        console.log("CloudflareTurnstile unmounting");
        // Cleanup widget when component unmounts
        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.reset(widgetIdRef.current);
          } catch (error) {
            console.error("Failed to reset Turnstile:", error);
          }
        }
        widgetIdRef.current = null;
      };
    }, []); // Remove renderWidget from dependencies to prevent re-mounting

    const resetWidget = useCallback(() => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch (error) {
          console.error("Failed to reset Turnstile:", error);
        }
      }

      // Reset state to allow re-rendering
      widgetIdRef.current = null;

      // Re-render the widget after a short delay
      setTimeout(() => {
        renderWidget();
      }, 50);
    }, [renderWidget]);

    // Expose reset method through ref
    useImperativeHandle(ref, () => ({
      reset: resetWidget,
    }));

    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
        <div ref={containerRef} />
        {!window.turnstile && (
          <div style={{ color: "red", fontSize: "12px" }}>
            Cloudflare Turnstile script not loaded
          </div>
        )}
      </Box>
    );
  }
);

CloudflareTurnstile.displayName = "CloudflareTurnstile";

export default CloudflareTurnstile;
